/**
 * Per-page SEO + Open Graph + Twitter card hoisting.
 *
 * Why imperative (not React 19 native head hoisting)?
 *   React 19 hoists tags but does NOT dedupe `<meta>` by `name=` /
 *   `property=`. That leaves duplicate og:title tags in the DOM (one
 *   from /public/index.html as the global default, one from this
 *   component) and crawlers pick inconsistently.
 *
 *   Doing it imperatively in useEffect lets us *replace* the global
 *   defaults per-route while keeping `index.html`'s tags as a hard
 *   fallback for the very first paint and for any page that doesn't
 *   mount this component.
 *
 * Usage:
 *   <Seo
 *     title="Forge Ledger — Your data, kept safe"
 *     description="..."
 *     path="/security"
 *   />
 */
import { useEffect } from 'react';

const BRAND_ORIGIN = 'https://forgeledger.net';
const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_budget-forecast-27/artifacts/w1b6b3hu_mini.png';

const setMeta = (selector, attrName, attrValue, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setRobots = (noindex) => {
  let el = document.head.querySelector('meta[name="robots"]');
  if (noindex) {
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex,nofollow');
  } else if (el) {
    el.remove();
  }
};

const Seo = ({
  title,
  description,
  path = '',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
}) => {
  useEffect(() => {
    const url = `${BRAND_ORIGIN}${path}`;

    if (title) document.title = title;

    setMeta('meta[name="description"]', 'name', 'description', description || '');

    // Open Graph
    setMeta('meta[property="og:type"]', 'property', 'og:type', type);
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'Forge Ledger');
    setMeta('meta[property="og:title"]', 'property', 'og:title', title || '');
    setMeta('meta[property="og:description"]', 'property', 'og:description', description || '');
    setMeta('meta[property="og:url"]', 'property', 'og:url', url);
    setMeta('meta[property="og:image"]', 'property', 'og:image', image);

    // Twitter / X
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title || '');
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description || '');
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image);

    setLink('canonical', url);
    setRobots(noindex);
  }, [title, description, path, image, type, noindex]);

  return null;
};

export default Seo;
